/**
 * WorkspacesProSection — 3 workspaces, zero mixing: copy left, oblique render right.
 * Visual: WorkspaceSwitcherFrame mock (no click/navigate; demo safe).
 */

import * as React from "react";
import { motion } from "framer-motion";
import { CheckCircle2 } from "lucide-react";
import { ObliqueAppFrame } from "@/components/landing/ObliqueAppFrame";
import { LandingOnboardingPreview } from "@/components/landing/LandingOnboardingPreview";
import { DemoInteractionGuard } from "@/components/landing/demos/DemoInteractionGuard";

const transition = { duration: 0.16, ease: [0, 0, 0.2, 1] as [number, number, number, number] };
const viewport = { once: true, margin: "-15% 0px -15% 0px" as const };

const BULLETS = [
  "Business 1 — dedicated pipeline & metrics",
  "Business 2 — isolated follow-ups & tasks",
  "My workspace — personal/default workspace",
] as const;

export function WorkspacesProSection() {
  return (
    <section className="py-24 md:py-32 px-4 bg-background overflow-visible">
      <div className="container mx-auto max-w-[var(--landing-max-width)] overflow-visible">
        <div className="grid grid-cols-1 md:grid-cols-12 items-center gap-10 md:gap-14 overflow-visible">
          {/* Copy right on desktop */}
          <motion.div
            className="md:col-span-5 md:col-start-8 order-2 md:order-2"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={viewport}
            transition={{ duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }}
          >
            <h2 className="text-[var(--font-h2)] font-semibold tracking-[var(--tracking-tighter)] mb-4 leading-[var(--leading-headline)] text-foreground">
              3 workspaces, zero mixing
            </h2>
            <p className="text-[var(--font-body-lg)] text-muted-foreground mb-6 leading-[var(--leading-body)] tracking-[var(--tracking-normal)]">
              Run multiple businesses without mixing pipeline, revenue, or follow-ups. Switch instantly — Business 1, Business 2, or My workspace.
            </p>
            <ul className="space-y-3">
              {BULLETS.map((bullet, index) => (
                <motion.li
                  key={index}
                  className="flex items-start gap-3"
                  initial={{ opacity: 0, x: -12 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={viewport}
                  transition={{ delay: index * 0.1, duration: 0.4 }}
                >
                  <CheckCircle2 className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                  <span className="text-foreground/80 leading-[var(--leading-body)]">{bullet}</span>
                </motion.li>
              ))}
            </ul>
          </motion.div>
          {/* Render left on desktop — oblique frame (demo safe, no click/navigate); hover/parallax premium */}
          <motion.div
            className="relative flex items-center justify-center md:col-span-7 md:col-start-1 order-1 md:order-1 self-center overflow-visible py-6 md:py-8"
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={viewport}
            transition={transition}
          >
            <div className="w-full max-w-[520px] pointer-events-auto" data-demo-guard>
              <DemoInteractionGuard>
                <ObliqueAppFrame badge="PRO" className="w-full">
                  <div className="aspect-[4/3] w-full min-h-[300px] max-h-[440px] overflow-hidden rounded-b-[20px] bg-[#0b0b0f] flex items-center justify-center">
                    <div className="h-full w-full max-h-full max-w-full scale-[0.98] origin-center p-2">
                      <LandingOnboardingPreview />
                    </div>
                  </div>
                </ObliqueAppFrame>
              </DemoInteractionGuard>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
